import type { RowReadingDto } from '@miyuki/types';
import { BeadRunList, type BeadRun } from '@miyuki/ui';
import { ArrowLeftIcon, ArrowRightIcon } from 'lucide-react';

import { t } from '@/lib/i18n';

/**
 * Qué ensartar en la fila que toca, y en qué orden.
 *
 * Va sobre la franja inferior y no sobre el lienzo para no tapar justamente la
 * fila que destaca (RF-34). Traduce los tramos del dominio a lo que el
 * componente de presentación entiende: un color resuelto, un número y un texto.
 */
export interface TrackingSummaryProps {
  /** Número de fila tal como se muestra, o `null` si el diseño terminó. */
  readonly rowNumber: number | null;
  readonly reading: RowReadingDto | null;
  readonly colors: readonly string[];
  readonly alternates: boolean;
  readonly onToggleAlternates: (alternates: boolean) => void;
}

export function TrackingSummary({
  rowNumber,
  reading,
  colors,
  alternates,
  onToggleAlternates,
}: TrackingSummaryProps) {
  // Los tramos llevan la **posición** del color en la paleta, no su nombre: la
  // paleta no tiene nombres (RF-14 de 001), y un ordinal es la segunda señal
  // que distingue dos tramos sin depender de verlos distintos (RF-31).
  const runs: readonly BeadRun[] =
    reading?.runs.map((run) =>
      run.value === null
        ? { color: null, count: run.count, label: t('trackingGapCount', { n: run.count }) }
        : {
            color: colors[run.value] ?? null,
            count: run.count,
            label: t('trackingRunCount', { n: run.count, color: run.value + 1 }),
            ordinal: String(run.value + 1),
          },
    ) ?? [];

  const rightToLeft = reading?.rightToLeft ?? true;
  const DirectionIcon = rightToLeft ? ArrowLeftIcon : ArrowRightIcon;

  return (
    <section className="bg-background/90 border-border shrink-0 border-t px-3 py-2 backdrop-blur">
      {reading !== null && rowNumber !== null && (
        <>
          <div className="text-muted-foreground mb-1.5 flex items-center gap-1.5 text-xs">
            <DirectionIcon className="size-3.5" aria-hidden="true" />
            <span>{rightToLeft ? t('trackingFromRight') : t('trackingFromLeft')}</span>
          </div>

          <div className="overflow-x-auto">
            <BeadRunList runs={runs} label={t('trackingSummary', { n: rowNumber })} />
          </div>
        </>
      )}

      {/* El sentido se puede cambiar sin salir del modo: quien lo descubre
          equivocado está tejiendo y no quiere volver al diseño (RF-28e). */}
      <button
        type="button"
        role="switch"
        aria-checked={alternates}
        aria-label={t('trackingAlternates')}
        onClick={() => onToggleAlternates(!alternates)}
        className="text-muted-foreground mt-2 flex min-h-11 items-center gap-2 text-xs"
      >
        <span
          aria-hidden="true"
          data-state={alternates ? 'on' : 'off'}
          className="border-border data-[state=on]:bg-primary relative h-5 w-9 shrink-0 rounded-full border transition-colors"
        >
          <span
            className={`bg-background absolute top-0.5 size-3.5 rounded-full shadow transition-all ${
              alternates ? 'left-4.5' : 'left-0.5'
            }`}
          />
        </span>
        <span>{t('trackingAlternates')}</span>
      </button>
    </section>
  );
}
